import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStreamSocket } from '../socket/useStreamSocket';
import { useAgentStreamPage } from './useAgentStream';
import {
  mergeEarlierPageInfo,
  mergeFreshWindow,
  normalizeStreamResponse,
  subscriptionForPage,
} from './stream-paging';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { getSocketManager } from '@/lib/socketManager';
import { deduplicateEvents } from '@reflex/ui/client/event-utils';
import type { AgentStreamPageInfo } from '@reflex/shared';
import type { AxonEvent } from '@/lib/types';

export function useAgentStreamSocket(agentId: string | null, streamId: string | null): void {
  const queryClient = useQueryClient();

  // The subscription waits for the initial stream fetch (page still
  // `undefined`): a paged agent must subscribe at its window head
  // (`afterSequence`) or the socket's history replay would send the
  // browser the full history the paged fetch kept off the client. The
  // win is client-side only — the server still backfills and pins the
  // whole log to serve the replay. Legacy agents (page `null`) subscribe
  // with a full replay exactly as before.
  const page = useAgentStreamPage(agentId);
  const subscription = subscriptionForPage(streamId, page);
  useStreamSocket({
    streamId: subscription.streamId,
    queryKey: [...queryKeys.stream(agentId ?? '')],
    afterSequence: subscription.afterSequence,
  });

  useEffect(() => {
    if (!agentId) return;

    const manager = getSocketManager();
    const handler = () => {
      api
        .getStreamPaged(agentId)
        .then((response) => {
          // Server snapshot first: it is the canonical ordering, and anything
          // the client holds that the snapshot lacks is by definition newer
          // (delivered over the socket while the request was in flight). The
          // other way round, an event missed during the gap would be appended
          // after events that already followed it, and consumers reading the
          // array as a timeline would see a completion land before the call it
          // completes. In paged mode the snapshot is only the newest window:
          // the cache keeps its older loaded pages and the window appends —
          // see {@link mergeFreshWindow} for why the order flips.
          const { events, page: fresh } = normalizeStreamResponse(response);
          queryClient.setQueryData<AxonEvent[]>([...queryKeys.stream(agentId)], (old) =>
            fresh
              ? mergeFreshWindow(old ?? [], events)
              : deduplicateEvents([...events, ...(old ?? [])]),
          );
          if (fresh) {
            queryClient.setQueryData<AgentStreamPageInfo | null>(
              [...queryKeys.streamPage(agentId)],
              // The loaded range keeps its older pages; only the head moved.
              (old) => (old ? mergeEarlierPageInfo(fresh, old) : fresh),
            );
          }
        })
        .catch(() => {
          // reconciliation failure is non-fatal; next events will arrive via WS
        });
    };
    manager.addReconnectCallback(handler);

    return () => manager.removeReconnectCallback(handler);
  }, [agentId, queryClient]);
}
